import { useNavigate } from 'react-router-dom';
import { Menu, PanelLeftClose, LogOut, ChevronDown, Plus, Target, Briefcase, ClipboardList, FolderKanban, CalendarDays } from 'lucide-react';
import { useUIStore } from '@/shared/stores/ui.store';
import { useAuthActions, useUser } from '@/shared/hooks/useAuth';
import { Avatar } from '@/shared/components/ui/Avatar';
import { Dropdown } from '@/shared/components/ui/Dropdown';
import { Badge } from '@/shared/components/ui/Badge';
import { NotificationBell } from '@/features/activity/components/NotificationBell';
import { ROUTES } from '@/routes/paths';
import { identityOf } from '@/shared/permissions/identity';

export function Topbar() {
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const toggleCollapsed = useUIStore((s) => s.toggleSidebarCollapsed);
  const user = useUser();
  const { logout } = useAuthActions();
  const navigate = useNavigate();

  const identity = identityOf(user);

  const onLogout = async () => {
    await logout();
    navigate(ROUTES.login, { replace: true });
  };

  // Quick Create routes to the relevant module (the module owns the create flow).
  const quickCreate = [
    { label: 'New Lead', icon: <Target className="h-4 w-4" />, onClick: () => navigate(ROUTES.leads) },
    { label: 'New Deal', icon: <Briefcase className="h-4 w-4" />, onClick: () => navigate(ROUTES.deals) },
    { label: 'New Task', icon: <ClipboardList className="h-4 w-4" />, onClick: () => navigate(ROUTES.tasks) },
    { label: 'New Project', icon: <FolderKanban className="h-4 w-4" />, onClick: () => navigate(ROUTES.projects) },
    { label: 'New Meeting', icon: <CalendarDays className="h-4 w-4" />, onClick: () => navigate(ROUTES.meetings) },
  ];

  return (
    <header className="sticky top-0 z-20 flex h-14 items-center justify-between gap-3 border-b border-outline bg-surface/95 px-4 backdrop-blur">
      <div className="flex items-center gap-1.5">
        <button
          className="rounded-md p-2 text-on-surface-variant hover:bg-surface-variant lg:hidden"
          onClick={toggleSidebar}
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5" />
        </button>
        <button
          className="hidden rounded-md p-2 text-on-surface-variant hover:bg-surface-variant lg:block"
          onClick={toggleCollapsed}
          aria-label="Collapse sidebar"
        >
          <PanelLeftClose className="h-5 w-5" />
        </button>
      </div>

      <div className="flex items-center gap-1.5 sm:gap-2">
        {user && (
          <Dropdown
            align="right"
            trigger={
              <span className="inline-flex h-9 items-center gap-1.5 rounded-DEFAULT bg-primary px-3 text-data-md font-medium text-on-primary shadow-surface-1 transition-colors hover:bg-primary/90">
                <Plus className="h-4 w-4" />
                <span className="hidden sm:block">Create</span>
                <ChevronDown className="h-4 w-4 opacity-80" />
              </span>
            }
            items={quickCreate}
          />
        )}

        {user && <NotificationBell />}

        {user && (
          <Dropdown
            align="right"
            trigger={
              <span className="flex items-center gap-2 rounded-DEFAULT px-2 py-1 hover:bg-surface-variant">
                <Avatar name={user.displayName} size="sm" />
                <span className="hidden text-left sm:block">
                  <span className="block text-data-md font-medium leading-tight text-on-surface">
                    {user.displayName}
                  </span>
                  <span className="block text-label-caps uppercase leading-tight text-on-surface-variant">
                    {identity.designation}
                  </span>
                </span>
                <ChevronDown className="h-4 w-4 text-on-surface-variant" />
              </span>
            }
            items={[{ label: 'Log out', icon: <LogOut className="h-4 w-4" />, onClick: onLogout, danger: true }]}
          />
        )}

        <Badge tone="primary" className="hidden md:inline-flex">
          {identity.designation}
        </Badge>
      </div>
    </header>
  );
}
